import Refresh from "@mui/icons-material/Refresh";
import { Tooltip } from "@mui/material";
import Cross from "assets/img/svg/cross";
import LoadingDots from "components/LoadingDots";
import { formatFileName, getJsonOrFalse, isObject } from "helpers/utils";
import { forwardRef, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { uploadFile } from "store/documentStore";
import { useAppDispatch } from "store/store";

import StyledButton from "../StyledButton";
import styles from "./style.module.scss";

interface IUploadFileBtn {
    /**
     * Text for the label (optional)
     */
    label?: string;
    control: any;
    controlName: string;
    watch: any;
    setValue: any;
    /**
     * Index of the field withing the customFields array (useForm)
     */
    index: any;
    disabled?: boolean;
}
/**
 * Component to upload file with button (no drag and drop). Can only be used within forms (as uses Controller)
 */
const UploadFileBtn = forwardRef<any, IUploadFileBtn>(function UploadFileBtn(props: IUploadFileBtn, ref) {
    const { label, control, controlName, watch, index, setValue, disabled = false } = props;
    const dispatch = useAppDispatch();
    const [t] = useTranslation();
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const [fileName, setFileName] = useState("");
    const [fileURL, setFileURL] = useState(""); // Currently this isn't being used, but just in case, the logic of saving URL is already implemented.
    const [statusUpload, setStatusUpload] = useState("");

    /**
     * Sends data of file to backend to get a link that can be saved into Title/Beneficiary.
     * @param files Data of file that was uploaded.
     */
    const handleFileUpload = (files: any) => {
        const file = files[0];
        const fileName = file.name ? file.name : "";
        const formData = new FormData();
        formData.append("file", file, fileName);

        dispatch(uploadFile({ formData })).then((resp) => {
            if (resp.meta.requestStatus === "fulfilled") {
                setFileName(fileName);
                resp.payload && setFileURL(resp?.payload.imagePath as string);
                const data = { fileName: fileName, link: resp.payload.imagePath };
                setValue(`customFields.${index}.value`, JSON.stringify(data), { shouldDirty: true });
                setStatusUpload("fulfilled");
            }
        });
    };

    /**
     * Handles removing the file that was uploaded > saving an empty string to form.
     */
    const handleRemovingFile = () => {
        setValue(`customFields.${index}.value`, "", { shouldDirty: true });
        setFileName("");
        setFileURL("");
    };

    // Data of file uploaded and saved to corresponding field within form (can be string or JSON depending on moment)
    const uploadedFile = watch(`customFields.${index}.value`);

    // Parsed uploadedFile (when adding a new file or changing the one already there)
    const formFileData = getJsonOrFalse(uploadedFile) ?? null;

    useEffect(() => {
        // Needed for displaying name of file when first displaying edit form:
        if (uploadedFile && isObject(uploadedFile)) {
            setFileName(uploadedFile.fileName);
            setFileURL(uploadedFile.link);
            // To save file name on creation or edit of file:
        } else if (uploadedFile && formFileData) {
            setFileName(formFileData.fileName);
            setFileURL(formFileData.link);
        } else {
            // Needed to reset data when switching pages on edit:
            setFileName("");
            setFileURL("");
        }
    }, [uploadedFile]);

    return (
        <div className={styles["upload-file-container"]} ref={ref}>
            <Controller
                name={controlName}
                control={control}
                render={() => {
                    return (
                        <>
                            {label && (
                                <label htmlFor={controlName} className={styles["upload-file-label"]}>
                                    {label}
                                </label>
                            )}
                            <div className={styles["upload-file-btn"]}>
                                {statusUpload === "pending" ? (
                                    <LoadingDots />
                                ) : (
                                    <>
                                        {!fileName && (
                                            <Tooltip title={disabled && t("LogIn.NotAuthorizedException")}>
                                                <div>
                                                    <StyledButton
                                                        onClick={() => fileInputRef.current?.click()}
                                                        className={styles["button"]}
                                                        buttonType="outlined"
                                                        disabled={disabled}
                                                    >
                                                        {t("CustomFields.ChooseFile")}
                                                    </StyledButton>
                                                </div>
                                            </Tooltip>
                                        )}
                                        <span className={`${fileName ? styles["big-text"] : styles["small-text"]}`}>
                                            {fileName ? formatFileName(fileName) : t("CustomFields.NoFileChosen")}
                                        </span>
                                        {fileName && !disabled && (
                                            <>
                                                <Refresh
                                                    className={styles["resfresh-icon"]}
                                                    onClick={() => fileInputRef.current?.click()}
                                                />
                                                <Cross
                                                    style={{ width: "15px", height: "15px", cursor: "pointer" }}
                                                    onClick={() => handleRemovingFile()}
                                                />
                                            </>
                                        )}
                                    </>
                                )}
                            </div>
                            <input
                                ref={fileInputRef}
                                style={{ display: "none" }}
                                onChange={(event) => {
                                    setStatusUpload("pending");
                                    handleFileUpload(event.target.files);
                                }}
                                accept=".pdf, .png, .jpg, .jpeg"
                                id="upload-file"
                                type="file"
                            />
                        </>
                    );
                }}
            />
        </div>
    );
});

export default UploadFileBtn;
